import { Fragment, useEffect, useRef, useState } from 'react';
import type { ChangeEvent } from 'react';
import { Popover, Transition } from '@headlessui/react';
import { usePopper } from 'react-popper';
import { matchSorter } from 'match-sorter';
import clsx from 'clsx';
import useTranslation from 'next-translate/useTranslation';

// Component
import Input from '@/components/base/Input';
import Icon from '@/components/base/Icon';
import { AvatarMenu } from '@/components/composite/SocialProfiles/Components/AvatarMenu';

//libs
import { sortKeyProfile, sortKeyGroup } from '@/lib/menu/RightMenu/matchSorterKey';

//types
import type { SocialProfileType } from '@/types/socialProfileType';
import type { SpGroupType } from '@/utils/hooks/swr/group/useSPGroups';
import type { DefaultAvatarProps } from '@/components/base/Avatar';

type PopoverItemProps = DefaultAvatarProps & {
    data: SocialProfileType | SpGroupType;
    onClick: () => void;
};
const PopoverItem = ({ data, onClick, ...restProps }: PopoverItemProps) => {
    return (
        <li>
            <button className="w-full" onClick={onClick}>
                <AvatarMenu
                    hasHover={false}
                    variant={'name' in data ? 'group' : 'social-profile'}
                    data={data}
                    compact={false}
                    {...restProps}
                />
            </button>
        </li>
    );
};

type PopoverListContentProps = Pick<RightMenuPopoverProps, 'variant' | 'updateActiveItem'> &
    Pick<PanelContentProps, 'updatePopperPosition' | 'close'> & {
        additionalData: (SocialProfileType | SpGroupType)[];
        displayedData: (SocialProfileType | SpGroupType)[];
        activeItem?: SocialProfileType | SpGroupType;
    };
function PopoverListContent({
    additionalData,
    displayedData,
    activeItem,
    updatePopperPosition,
    updateActiveItem,
    variant,
    close
}: PopoverListContentProps) {
    const { t } = useTranslation('commons');
    const inputSearchRef = useRef<HTMLInputElement>();
    const defaultValues = {
        active: activeItem ? [activeItem] : [],
        additional: additionalData,
        displayed: displayedData
    };

    const [itemsList, setItemsList] = useState(defaultValues);

    const filterLists = (event: ChangeEvent<HTMLInputElement>) => {
        const searchValue: ChangeEvent<HTMLInputElement>['target']['value'] = event.target.value;
        // Reset lists if the input is empty
        if (!searchValue) {
            setItemsList(defaultValues);
        }

        let socialProfilesAdditionalFiltered: (SocialProfileType | SpGroupType)[] = [];
        let socialProfilesDisplayedFiltered: (SocialProfileType | SpGroupType)[] = [];
        let activeSocialProfileFiltered: (SocialProfileType | SpGroupType)[] = [];
        if (variant === 'social-profile') {
            // Filter on Additional social profiles
            socialProfilesAdditionalFiltered = matchSorter(
                additionalData as SocialProfileType[],
                searchValue,
                sortKeyProfile
            );

            // Filter on Already displayed social profiles
            socialProfilesDisplayedFiltered = matchSorter(
                displayedData as SocialProfileType[],
                searchValue,
                sortKeyProfile
            );

            // Filter on Already displayed social profiles
            activeSocialProfileFiltered = matchSorter(
                [activeItem] as SocialProfileType[],
                searchValue,
                sortKeyProfile
            );
        }
        if (variant === 'group') {
            // Filter on Additional social profiles
            socialProfilesAdditionalFiltered = matchSorter(
                additionalData as SpGroupType[],
                searchValue,
                sortKeyGroup
            );

            // Filter on Already displayed social profiles
            socialProfilesDisplayedFiltered = matchSorter(
                displayedData as SpGroupType[],
                searchValue,
                sortKeyGroup
            );

            // Filter on Already displayed social profiles
            activeSocialProfileFiltered = matchSorter(
                [activeItem] as SpGroupType[],
                searchValue,
                sortKeyGroup
            );
        }
        setItemsList({
            active: activeSocialProfileFiltered,
            additional: socialProfilesAdditionalFiltered,
            displayed: socialProfilesDisplayedFiltered
        });

        updatePopperPosition();
    };

    return (
        <div className="w-80 space-y-3.5 px-2 pb-4 pt-5">
            <div className="px-3">
                <Input
                    id="search-input"
                    inputStyle="search"
                    noBorder={true}
                    ref={inputSearchRef}
                    aria-label={
                        variant === 'social-profile'
                            ? t('add_social_profiles.search_social_profiles')
                            : t('add_social_profiles.search_groups')
                    }
                    onChange={filterLists}
                    placeholder={
                        variant === 'social-profile'
                            ? t('add_social_profiles.search_social_profiles')
                            : t('add_social_profiles.search_groups')
                    }
                    beforeInput={<Icon icon="search" />}
                />
            </div>
            <ul className="scrollbar max-h-72 space-y-3.5 overflow-x-hidden px-3 pb-1 pt-2">
                {itemsList.additional?.map((item) => (
                    <PopoverItem
                        data-testid="popover-item-not-displayed"
                        data={item}
                        key={item.id}
                        onClick={() => {
                            updateActiveItem(item);
                            close();
                        }}
                    />
                ))}
                {itemsList?.displayed?.length || itemsList?.active?.length ? (
                    <li
                        className="flex justify-center rounded-m bg-icoBlue-100 p-2 text-xs text-icoBlue dark:bg-icoBlue/20"
                        data-testid="disclaimer-already-displayed">
                        {t('list_social_profiles.profiles_below_already_displayed')}
                    </li>
                ) : null}
                {itemsList?.active?.map((item) => {
                    return (
                        <PopoverItem
                            data-testid="popover-active-element"
                            data={item}
                            selected
                            key={item.id}
                            onClick={close}
                        />
                    );
                })}
                {itemsList?.displayed?.map((item) => {
                    return (
                        <PopoverItem
                            data-testid="popover-item-displayed"
                            data={item}
                            key={item.id}
                            onClick={() => {
                                updateActiveItem(item);
                                close();
                            }}
                        />
                    );
                })}
            </ul>
        </div>
    );
}

type PanelContentProps = Pick<
    RightMenuPopoverProps,
    'itemsList' | 'updateActiveItem' | 'variant' | 'activeItem'
> & {
    close: () => void;
    updatePopperPosition: ReturnType<typeof usePopper>['update'];
};
const PanelContent = ({
    activeItem,
    updatePopperPosition,
    close,
    itemsList,
    updateActiveItem,
    variant
}: PanelContentProps) => {
    let displayedItems: (SocialProfileType | SpGroupType)[] = [];
    let additionalItems: (SocialProfileType | SpGroupType)[] = [];

    if (variant === 'group') {
        const intermediaryData = itemsList as SpGroupType[];
        displayedItems = intermediaryData.filter(
            (_, index: number) => index < (activeItem ? 4 : 5)
        );
        additionalItems = intermediaryData.filter(
            (_, index: number) => index > (activeItem ? 3 : 4)
        );
    }
    if (variant === 'social-profile') {
        const intermediaryData = itemsList as SocialProfileType[];
        displayedItems = intermediaryData.filter(
            (_, index: number) => index < (activeItem ? 4 : 5)
        );
        additionalItems = intermediaryData.filter(
            (_, index: number) => index > (activeItem ? 3 : 4)
        );
    }

    useEffect(() => {
        // Position problem so force the update to center the content
        if (typeof updatePopperPosition === 'function') {
            updatePopperPosition();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <div
            className={clsx(
                'rounded-md border font-GSemiBold shadow-icoXL backdrop-blur-sm firefox:bg-gradient-to-tr',
                'border-white bg-white/80 firefox:from-icoGrayFirefox-300 firefox:to-icoWhiteFirefox dark:border-icoDarkMode-deepBlack dark:bg-icoDarkMode-deepBlack/80 dark:firefox:from-icoDarkMode-deepBlack dark:firefox:to-icoDarkMode-anthracite'
            )}>
            <PopoverListContent
                additionalData={additionalItems}
                displayedData={displayedItems}
                activeItem={activeItem}
                updatePopperPosition={updatePopperPosition}
                close={close}
                updateActiveItem={updateActiveItem}
                variant={variant}
            />
        </div>
    );
};

type RightMenuPopoverProps = {
    activeItem?: SocialProfileType | SpGroupType;
    nbItems: number;
    itemsList: (SocialProfileType | SpGroupType)[];
    updateActiveItem: (item: SocialProfileType | SpGroupType) => void;
    variant: 'social-profile' | 'group';
};
export function RightMenuPopover({
    activeItem,
    nbItems,
    itemsList,
    updateActiveItem,
    variant = 'social-profile'
}: RightMenuPopoverProps) {
    const [referenceElement, setReferenceElement] = useState<HTMLButtonElement>();
    const [popperElement, setPopperElement] = useState<Parameters<typeof usePopper>[1]>();
    const [showError, setShowError] = useState(false);
    const { styles, attributes, update } = usePopper(referenceElement, popperElement, {
        placement: 'left',
        modifiers: [
            {
                name: 'offset',
                options: {
                    offset: [0, 20]
                }
            }
        ]
    });

    useEffect(() => {
        //no need to check anything for groups
        if (variant === 'group') {
            return;
        }
        const intermediaryList = itemsList as SocialProfileType[];
        let error = false;
        for (let i = 4; i < intermediaryList?.length + 1; i++) {
            if (intermediaryList[i]?.auth_status?.type === 'error') {
                error = true;
                break;
            }
        }

        setShowError(error);
    }, [itemsList, variant]);

    //Remove active item from list of items to display since it's displayed independently
    let filteredList: (SocialProfileType | SpGroupType)[] = [];
    if (variant === 'group') {
        const intermediaryList = itemsList as SpGroupType[];
        filteredList = intermediaryList.filter((item) => item.id !== activeItem?.id);
    }
    if (variant === 'social-profile') {
        const intermediaryList = itemsList as SocialProfileType[];
        filteredList = intermediaryList.filter((item) => item.id !== activeItem?.id);
    }

    return (
        <Popover className="w-full" data-testid="popover-wrapper">
            {({ open }) => (
                <>
                    <Popover.Button
                        data-testid="popover-button"
                        ref={setReferenceElement}
                        className={clsx(
                            'flex h-11.5 w-11.5 items-center justify-center rounded-full border-2 bg-icoGray-100 text-sm text-icoBlue focus:ring-offset-0 dark:bg-icoDarkMode-anthracite',
                            {
                                'border-white dark:border-icoDarkMode-deepBlack': !open,
                                'border-icoBlue': open,
                                'rounded-full': variant === 'social-profile',
                                'rounded-md': variant === 'group'
                            }
                        )}>
                        +{nbItems}
                        {showError ? (
                            <div
                                role="alert"
                                className={clsx(
                                    'absolute -left-2.5 inline-flex h-m w-m items-center justify-center bg-icoRed text-center text-xxs text-white ring-2 ring-inset ring-white dark:ring-icoDarkMode-deepBlack',
                                    {
                                        'rounded-full': variant === 'social-profile',
                                        'rounded-md': variant === 'group'
                                    }
                                )}>
                                <span className="inline-block align-middle leading-none">!</span>
                            </div>
                        ) : null}
                    </Popover.Button>
                    <div ref={setPopperElement} style={styles.popper} {...attributes.popper}>
                        <Transition
                            as={Fragment}
                            enter="ease-out duration-200"
                            enterFrom="opacity-0 translate-y-1"
                            enterTo="opacity-100 translate-y-0"
                            leave="ease-in duration-150"
                            leaveFrom="opacity-100 translate-y-0"
                            leaveTo="opacity-0 translate-y-1">
                            <Popover.Panel role="dialog">
                                <PanelContent
                                    activeItem={activeItem}
                                    updatePopperPosition={update}
                                    close={() => referenceElement.click()}
                                    itemsList={filteredList}
                                    updateActiveItem={updateActiveItem}
                                    variant={variant}
                                />
                            </Popover.Panel>
                        </Transition>
                    </div>
                </>
            )}
        </Popover>
    );
}
